/**
 * Login Google (GIS) — LAC-045.
 * Por quê script GIS e não OAuth redirect: SPA JWT-stateless; API só valida id_token.
 */

export type GoogleCredentialResponse = {
  credential?: string;
  select_by?: string;
};

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    context?: string;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      theme?: string;
      size?: string;
      text?: string;
      shape?: string;
      width?: number;
      locale?: string;
    }
  ) => void;
  prompt?: (momentListener?: (notification: {
    isNotDisplayed: () => boolean;
    isSkippedMoment: () => boolean;
    getNotDisplayedReason?: () => string;
  }) => void) => void;
  cancel?: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId;
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise: Promise<void> | null = null;

export function getGoogleClientId(): string {
  return (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
}

export function isGoogleLoginEnabled(): boolean {
  return getGoogleClientId().length > 0;
}

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("google_gis_load_failed"))
      );
      if (window.google?.accounts?.id) {
        resolve();
      }
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("google_gis_load_failed"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * One Tap (prompt flutuante) — além do botão “Continuar com Google”.
 */
export async function promptGoogleOneTap(
  onCredential: (idToken: string) => void
): Promise<() => void> {
  const clientId = getGoogleClientId();
  if (!clientId) {
    return () => undefined;
  }

  await loadGisScript();
  const id = window.google?.accounts?.id;
  if (!id?.prompt) {
    return () => undefined;
  }

  id.initialize({
    client_id: clientId,
    callback: (response) => {
      const token = response.credential?.trim();
      if (token) {
        onCredential(token);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    context: "signin",
  });

  id.prompt();

  return () => {
    id.cancel?.();
  };
}

/**
 * Renderiza o botão oficial Google no container.
 * @returns cleanup (remove listeners / limpa nó)
 */
export async function mountGoogleSignInButton(
  container: HTMLElement,
  onCredential: (idToken: string) => void,
  options?: { locale?: string; width?: number }
): Promise<() => void> {
  const clientId = getGoogleClientId();
  if (!clientId) {
    return () => undefined;
  }

  await loadGisScript();
  const id = window.google?.accounts?.id;
  if (!id) {
    throw new Error("google_gis_unavailable");
  }

  container.innerHTML = "";

  id.initialize({
    client_id: clientId,
    callback: (response) => {
      const token = response.credential?.trim();
      if (token) {
        onCredential(token);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  id.renderButton(container, {
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    width: options?.width ?? 320,
    locale: options?.locale,
  });

  return () => {
    container.innerHTML = "";
  };
}
